import type { Identifier } from "ra-core";

/**
 * How several selected tags combine.
 *
 * - `any` matches a contact carrying at least one of them. PostgREST `ov`
 *   (overlaps).
 * - `all` matches a contact carrying every one of them. PostgREST `cs`
 *   (contains).
 *
 * `any` is the default because the tag vocabulary has mutually exclusive
 * families: tier-0-existing through tier-4-nurture, and the vendor tags
 * (nvidia, sony, adobe). Under `all` those pairs return nothing, which is a
 * natural thing to try and a bad thing to get. See LCATOMICZO-32.
 */
export type TagMatchMode = "any" | "all";

export const TAG_FILTER_KEY: Record<TagMatchMode, string> = {
  any: "tags@ov",
  all: "tags@cs",
};

export const DEFAULT_TAG_MATCH_MODE: TagMatchMode = "any";

type FilterValues = Record<string, unknown>;

/**
 * Serialises ids the way PostgREST reads an array literal: `{1,2,3}`.
 * The provider passes the string through untouched, so `tags@ov` with
 * `"{1,2}"` becomes `tags=ov.{1,2}` on the wire.
 */
export function serializeTagIds(ids: Identifier[]): string {
  return `{${ids.join(",")}}`;
}

export function parseTagIds(value: unknown): Identifier[] {
  if (typeof value !== "string") return [];
  const inner = value.trim().replace(/^\{/, "").replace(/\}$/, "");
  if (inner === "") return [];
  return inner
    .split(",")
    .map((part) => part.trim())
    .filter((part) => part !== "")
    .map((part) => {
      const asNumber = Number(part);
      return Number.isNaN(asNumber) ? part : asNumber;
    });
}

/**
 * Reads whichever of the two keys is present.
 *
 * Both are accepted on the way in so a bookmarked URL written before the mode
 * switch existed, which can only carry `tags@cs`, still shows its tags in the
 * sidebar rather than silently filtering with nothing selected.
 */
export function readTagFilter(filterValues: FilterValues | undefined): {
  ids: Identifier[];
  mode: TagMatchMode;
} {
  const values = filterValues ?? {};
  if (TAG_FILTER_KEY.all in values) {
    return { ids: parseTagIds(values[TAG_FILTER_KEY.all]), mode: "all" };
  }
  return {
    ids: parseTagIds(values[TAG_FILTER_KEY.any]),
    mode: DEFAULT_TAG_MATCH_MODE,
  };
}

/**
 * Returns the next filter object. Both keys are dropped first, so switching
 * mode never leaves the old one behind to be ANDed with the new one, and an
 * empty selection removes the filter rather than sending `{}`.
 */
export function writeTagFilter(
  filterValues: FilterValues | undefined,
  ids: Identifier[],
  mode: TagMatchMode,
): FilterValues {
  const next = { ...(filterValues ?? {}) };
  delete next[TAG_FILTER_KEY.any];
  delete next[TAG_FILTER_KEY.all];
  if (ids.length > 0) {
    next[TAG_FILTER_KEY[mode]] = serializeTagIds(ids);
  }
  return next;
}
